<template>
  <KSelect
    ref="select"
    v-model="value"
    :id="id"
    :label="label"
    :name="name"
    :options="options"
    :option-label="optionLabel"
    :option-disabled="optionDisabled"
    :option-group-label="optionGroupLabel"
    :option-group-children="optionGroupChildren"
    :disabled="disabled"
    :hide-label="hideLabel"
    :placeholder="placeholder"
    :class="{ 'k-select--invalid': errorMessage }"
    :aria-describedby="errorMessage ? `${id}-error` : null"
  >
    <template #label>
      <label :class="['k-label', { 'sr-only': hideLabel }]" :for="id">
        {{ label }}
        <span v-if="requiredLabel" class="k-label--required">(required)</span>
        <span v-if="subtitle" class="k-label--subtitle">{{ subtitle }}</span>
      </label>
    </template>

    <template v-if="$slots.option" #option="slotProps">
      <slot name="option" :option="slotProps.option" :index="slotProps.index" />
    </template>

    <template v-if="$slots.optiongroup" #optiongroup="slotProps">
      <slot name="optiongroup" :option="slotProps.option" :index="slotProps.index" />
    </template>

    <template v-if="errorMessage" #message>
      <small class="k-select__message" :id="`${id}-error`">{{ errorMessage }}</small>
    </template>
  </KSelect>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { KSelect } from "@k2united/frontend_components";
import { useField } from "vee-validate";

interface Props {
  /**
   * Same as HTML element ID
   */
  id: string;

  /**
   * Select label
   */
  label: string;

  /**
   * Required for VeeValidate
   */
  name: string;

  /**
   * Select options
   */
  options: any[];

  /**
   * Property name to use as the label of an option
   */
  optionLabel?: string | ((data: any) => string);

  /**
   * Disable an option
   */
  optionDisabled?: string | ((data: any) => boolean);

  /**
   * Only show the label for screen readers
   */
  hideLabel?: boolean;

  /**
   * Default text to display when no option is selected
   */
  placeholder?: string;

  /**
   * Disables the select
   */
  disabled?: boolean;

  /**
   * Indicate that the field is required in the label
   */
  requiredLabel?: boolean;

  /**
   * Additional text to display under the label
   */
  subtitle?: string | null;

  /**
   * Option group label
   */
  optionGroupLabel?: string | ((data: any) => string);

  /**
   * Indicates the property name for the option group children
   */
  optionGroupChildren?: string | ((data: any) => any[]);
}

const props = withDefaults(defineProps<Props>(), {
  optionLabel: undefined,
  optionDisabled: undefined,
  hideLabel: false,
  placeholder: undefined,
  disabled: false,
  requiredLabel: false,
  subtitle: null,
  optionGroupLabel: undefined,
  optionGroupChildren: undefined
});

const select = ref();

const { value, errorMessage } = useField<string>(() => props.name, undefined, {
  validateOnValueUpdate: false
});

defineExpose({ select });
</script>

<style scoped lang="scss">
:deep(.k-select--invalid) {
  @include border-danger;
}

:deep(.k-select--invalid.k-select--focused) {
  box-shadow: 0 0 0 0.2rem $color-shadow-input-danger;
}

.k-select__message {
  @include error-message;
}

.k-label--required {
  @include body-x-small;
  color: $color-text-subtle;
}

.k-label--subtitle {
  color: $color-text-subtle;
  display: block;
  @include body-x-small;
}
</style>
